"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import type * as React from "react";

import { isNativeButtonRender } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  render,
  nativeButton,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      render={render}
      nativeButton={nativeButton ?? isNativeButtonRender(render)}
      {...props}
    />
  );
}

/**
 * A popup's material (glass.css). Menus and popovers are frosted (Archie,
 * 23 October 2026); a popover that is a form or holds heavy content (the
 * Turn list, "Used by", a table, a preview) passes `surface="solid"`, so
 * nothing behind it competes with what it holds.
 */
type PopupSurface = "frosted" | "solid";

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  collisionPadding,
  anchor,
  surface = "frosted",
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Popup> &
  Pick<
    React.ComponentProps<typeof PopoverPrimitive.Positioner>,
    "align" | "alignOffset" | "anchor" | "collisionPadding" | "side" | "sideOffset"
  > & { surface?: PopupSurface }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        anchor={anchor}
        className="isolate z-(--z-overlay)"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          data-surface={surface}
          className={cn(
            surface === "frosted" ? "frosted-surface" : "bg-popover",
            "w-72 origin-(--transform-origin) rounded-md border border-border p-4 text-popover-foreground shadow-md outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

/**
 * A popover row's hover and selected fill. Dark themes set the muted and
 * accent fills at the popover's own lightness, so a row marked with them
 * vanishes; a tint of the text colour, as menu items use, reads on any
 * surface. The dark variants override a ghost button's own hover.
 */
function popoverRowClass(selected: boolean) {
  return selected
    ? "bg-foreground/10 text-foreground hover:bg-foreground/10 dark:hover:bg-foreground/10"
    : "hover:bg-foreground/5 hover:text-foreground dark:hover:bg-foreground/5";
}

export { Popover, PopoverContent, PopoverTrigger, popoverRowClass, type PopupSurface };
